import { Button, Theme, Tooltip, Typography } from '@mui/material';
import type { Property } from 'csstype';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import StatButton from '../../elements/StatButton/StatButton';
import {
  ArtifactTopFiilter,
  ArtifactTopFiltersTypes,
} from '../types/artifacts-type';

const ArtifactsTopFilters: FC<
  React.PropsWithChildren<ArtifactsTopFiltersProps>
> = ({
  artifactTopFiilters,
  totalArtifacts,
  onInventoryFilterClick,
  appliedFilters,
}) => {
  const { classes, cx } = useStyles();
  return (
    <div className={classes.dashboardInventory}>
      <div className={classes.textContainer}>
        <Typography
          lineHeight='25px'
          fontWeight={400}
          variant='h1'
          data-testid='inventory-text'
          color='text.primary'>
          My Artifacts
        </Typography>
      </div>
      {totalArtifacts > 0 && (
        <Button
          className={cx(classes.artifactsCount, classes.artifactsText)}
          sx={{
            textTransform: 'none',
          }}>{`${totalArtifacts} Artifacts`}</Button>
      )}

      <div className={classes.filtersContainer}>
        {artifactTopFiilters.map((f, index) => {
          return (
            <Tooltip key={index} placement='top-start' title={f.label} arrow>
              <span>
                <StatButton
                  count={f.count}
                  label={f.label}
                  delta={f.delta}
                  trend={f.trend}
                  disabled={f.count === 0}
                  active={appliedFilters.includes(
                    f.name as ArtifactTopFiltersTypes,
                  )}
                  onClick={() =>
                    onInventoryFilterClick(f.name as ArtifactTopFiltersTypes)
                  }
                />
              </span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const useStyles = makeStyles<void, 'dashboardInventory'>()((theme: Theme) => ({
  dashboardInventory: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBlock: theme.spacing(1),
    paddingInline: theme.spacing(2.5),
    gap: theme.spacing(2),
    minHeight: 60,
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row' as Property.FlexDirection,
    },
  },
  textContainer: {
    whiteSpace: 'nowrap',
    paddingInlineEnd: theme.spacing(2),
  },
  relevantAppCount: {
    color: '#5059FF',
    textAlign: 'center',
    padding: theme.spacing(0.5, 0.5),
    background:
      theme.palette.mode === 'dark'
        ? '#1C1F2A'
        : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  notRelevantAppIcon: {
    fontSize: 8,
    cursor: 'pointer',
    width: '1rem',
    marginLeft: '3px',
  },
  appsTextIrrelevant: {
    marginRight: '10',
    color: `${theme.palette.dashInventory.textColor.colorRelevant} !important`,
    font: 'Roboto',
    fontSize: '0.875rem',
  },
  appsText: {
    color: `${theme.palette.dashInventory.textColor.colorAppsText} !important`,
    whiteSpace: 'nowrap',
    fontSize: 14,
    minWidth: '5rem',
  },

  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  artifactsCount: {
    color: '#5059FF',
    textAlign: 'center',
    padding: theme.spacing(0.5, 1.5),
    background:
      theme.palette.mode === 'dark'
        ? '#1C1F2A'
        : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  artifactsText: {
    color: `${theme.palette.dashInventory.textColor.colorAppsText} !important`,
    whiteSpace: 'nowrap',
    fontSize: 14,
    minWidth: '5rem',
  },
}));

export interface ArtifactsTopFiltersProps {
  artifactTopFiilters: ArtifactTopFiilter[];
  totalArtifacts: number;
  onInventoryFilterClick: (filterType: ArtifactTopFiltersTypes) => void;
  appliedFilters: Array<ArtifactTopFiltersTypes>;
}

export default ArtifactsTopFilters;
